import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { AutocompleteOption, StreamStatus } from '@app/shared/models/stream-status';
import { tap } from 'rxjs/operators';

@Component({
    selector: 'app-autocomplete',
    templateUrl: './autocomplete.component.html',
    styleUrls: ['./autocomplete.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class AutocompleteComponent implements OnInit {
    @Input() label: string;
    @Input() stream$: Observable<StreamStatus<AutocompleteOption[]>>;
    @Output() selectionChange: EventEmitter<unknown> = new EventEmitter<unknown>();

    @ViewChild('autocompleteInput') autocompleteInput: MatInput;

    options$: Observable<StreamStatus<AutocompleteOption[]>>;
    current: AutocompleteOption;
    filteredList$: Observable<AutocompleteOption[]>;

    private options: AutocompleteOption[];
    private filteredListSubject = new BehaviorSubject<AutocompleteOption[]>([]);

    ngOnInit(): void {
        this.filteredList$ = this.filteredListSubject.asObservable();

        this.options$ = this.stream$.pipe(
            tap((stream: StreamStatus<AutocompleteOption[]>) => {
                if (stream.loading) {
                    this.current = undefined;
                    this.options = [];
                    this.filteredListSubject.next([]);
                } else {
                    this.options = stream.value;
                    this.filteredListSubject.next(stream.value);
                }
            })
        );
    }

    getOptionLabel(option: AutocompleteOption): string {
        return option ? option.label : '';
    }

    onAutocompleteKeyUp(searchText: string): void {
        this.filteredListSubject.next(searchText ? this.filterOptions(searchText) : this.options);
    }

    onOptionChange(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        let option: AutocompleteOption;

        if (value) {
            const matchingOptions = this.filterOptions((event.target as HTMLInputElement).value);
            option = matchingOptions.length ? matchingOptions[0] : undefined;
        }

        this.current = option;
        this.selectionChange.emit(option?.value);
    }

    onOptionSelectionChange(event: MatAutocompleteSelectedEvent): void {
        this.selectionChange.emit(event.option.value);
    }

    private filterOptions(value: string): AutocompleteOption[] {
        const filterValue = value.toLowerCase();
        return this.options.filter((option) => option.label.toLowerCase().includes(filterValue));
    }
}
